import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  useMediaQuery,
  Dialog,
  Typography,
  Button,
  DialogTitle,
  DialogContentText,
  Box,
  Grid,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { ReactComponent as ACCheckCircleIcon } from '../../../icons/ACCheckCircleIcon.svg';
import ACSnackbar from './../../ACSnackbar';
import { useAccount } from './../../../contexts/Account';
import {
  getAdvisorPlanUpgrades,
  mutationConvertAdvisorPlanCodeEarly,
} from './../../../services/advisor_services';
import eventBus, { eventBusValues } from './../../../eventBus';
import { SnackbarType } from './../../../services/utils/types';
import {
  sendFSEventOnUpgrade,
  sendFSEventUpgradeViewed,
} from '../../../services/FullStory';
import LoadingLogoSkeleton from '../../SkeletonLoading/loadingLogoSkeleton';
import AdvisoryCloudLogo from '../../AdvisoryCloudLogo2';

const useStyles = ({ isXS }) => ({
  dialogContainer: {
    borderRadius: isXS ? '0px' : '15px',
    boxShadow: '0 5px 20px rgba(0, 0, 0, 0.07)',
    padding: isXS ? '5px' : '16px',
    textAlign: 'center',
    maxWidth: isXS ? '100%' : '800px',
  },
  container: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingLeft: isXS ? '10px' : '50px',
    paddingRight: isXS ? '10px' : '50px',
  },
  textHeadline: {
    fontWeight: 600,
    fontSize: '16px',
    mb: 1,
  },
  upgradeButton: {
    mb: 2,
    borderRadius: '50px',
    width: { xxs: '70px', md: '140px' },
    fontWeight: 600,
    fontSize: { xxs: '12px', md: '16px' },
    padding: '0px 0px',
  },
  cell: {
    py: 1,
    border: '1px solid #eee',
    height: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeIcon: {
    fontSize: '36px',
    color: 'red',
  },
});

const errorCodes = {
  recurlyError: 'RECURLY_ERROR',
  dynamoError: 'DYNAMO_DB_ERROR',
  salesforceError: 'SALESFORCE_ERROR',
};

let outsideSaveAdvisor = null;

const NewFreeTrialModal = () => {
  const { t } = useTranslation();
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const classes = useStyles({ isXS });
  const [fromBanner, setFromBanner] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackbarSuccess, setSnackbarSuccess] = useState(false);
  const [snackbarError, setSnackbarError] = useState(false);
  const [currentPlan, setCurrentPlan] = useState({});
  const [upgradePlan, setUpgradePlan] = useState(false);
  const {
    accountDetails,
    editAccountInformation,
    asLoggedUserDetails,
    type,
    currentUserInfo,
  } = useAccount();
  const { search } = useLocation();

  const [selectedPlan, setSelectedPlan] = useState(0);
  const [loading, setLoading] = useState(false);

  function getSelectedPlanLabel() {
    if (selectedPlan === 1) return 'Current';
    if (selectedPlan === 2) return 'Annual';
    if (selectedPlan === 3) return 'VIP';
    return 'Current';
  }

  useEffect(() => {
    async function fetchData() {
      const planResponses = await getAdvisorPlanUpgrades({
        RECURLY_PLAN_CODE: accountDetails.recurlyPlanCode,
      });
      if (planResponses.data && planResponses.data.getFreeTrialUpgrades) {
        const planCodesArr = planResponses.data.getFreeTrialUpgrades;
        setCurrentPlan(planCodesArr[0]);
        if (planCodesArr.length > 1) {
          setUpgradePlan(planCodesArr[1]);
        }
      }
    }

    eventBus.on(eventBusValues.triggerAdvisorUpgradeAction, (data) => {
      if (data) {
        setFromBanner(data.fromBanner || false);
        outsideSaveAdvisor = data.saveChanges || null;
      } else {
        setFromBanner(false);
      }
      setOpenModal(true);
    });

    const profileParam = new URLSearchParams(search).get('ft-pop-up');
    if (profileParam === 'banner') {
      setOpenModal(true);
    } else if (profileParam === 'gate') {
      setOpenModal(true);
    }

    fetchData();

    return () => {
      eventBus.remove(eventBusValues.triggerAdvisorUpgradeAction);
    };
  }, []);

  function handleClose() {
    setOpenModal(false);
    setSelectedPlan(0);
    outsideSaveAdvisor = null;
  }

  const sendFSEvent = (success, errorMessage = '', error = null) => {
    sendFSEventOnUpgrade({
      accountDetails,
      currentPlan: currentPlan,
      asLoggedUserDetails,
      newPlan: selectedPlan === 1 ? currentPlan : upgradePlan,
      source: fromBanner ? 'banner' : 'gate',
      type,
      upgradeType: 'freeTrial',
      success,
      errorMessage: success ? null : errorMessage,
      error: success ? null : error,
    });
  };

  const sendFSViewedEvent = (upgradeAttempted = false) => {
    sendFSEventUpgradeViewed({
      accountDetails,
      currentPlan: currentPlan,
      asLoggedUserDetails,
      currentUserInfo,
      source: fromBanner ? 'banner' : 'gate',
      type,
      upgradeType: 'free_trial',
      upgradeAttempted,
    });
  };

  async function upgradeCall() {
    sendFSViewedEvent(true);
    if (selectedPlan && !loading) {
      setLoading(true);
      if (outsideSaveAdvisor) {
        await outsideSaveAdvisor();
        outsideSaveAdvisor = null;
      }
      mutationConvertAdvisorPlanCodeEarly({
        ADVISOR_ID: accountDetails.id,
        IS_UPGRADE: selectedPlan === 2,
        IS_VIP: selectedPlan === 3,
      })
        .then((response) => {
          if (
            response &&
            response.data &&
            response.data.convertAdvisorPlanCodeEarly
          ) {
            const newAdvisor = response.data.convertAdvisorPlanCodeEarly;
            setSnackMessage(t('FREE_TRIAL_CONVERTION_SUCCESS'));
            setSnackbarSuccess(true);
            setLoading(false);
            editAccountInformation({
              freeTrialEndsOn: newAdvisor.freeTrialEndsOn,
              recurlyPlanCode: newAdvisor.recurlyPlanCode,
            });
            sendFSEvent(true);
            handleClose();
          }
        })
        .catch((e) => {
          let errText = '';
          try {
            const err = JSON.parse(e.errors[0].message);
            if (err.code === errorCodes.recurlyError) {
              errText = t('FREE_TRIAL_CONVERTION_ERROR_RECURLY');
            } else if (err.code === errorCodes.dynamoError) {
              errText = t('FREE_TRIAL_CONVERTION_ERROR_DYNAMO');
            } else if (err.code === errorCodes.salesforceError) {
              setSnackMessage(t('FREE_TRIAL_CONVERTION_SUCCESS'));
              setSnackbarSuccess(true);
              return;
            } else {
              errText = t('FREE_TRIAL_CONVERTION_ERROR_UNEXP');
            }
            setLoading(false);
            handleClose();
          } catch (err) {
            errText = t('FREE_TRIAL_CONVERTION_ERROR_UNEXP');
            setLoading(false);
          }
          sendFSEvent(false, errText, e);
          setSnackMessage(errText);
          setSnackbarError(true);
        });
    } else {
      setSnackMessage('Select an upgrade option to continue');
      setSnackbarSuccess(true);
      sendFSEvent(false, 'no plan selected', 'no plan selected');
    }
  }

  function getAnualSave() {
    try {
      const monthlyPerYear = currentPlan.recurlyPlanCodePrice * 12;
      return monthlyPerYear - upgradePlan.recurlyPlanCodePrice;
    } catch {
      return 0;
    }
  }

  return (
    <>
      <Dialog
        open={openModal}
        fullScreen={isXS}
        onClose={() => {
          if (loading) return;
          handleClose();
          sendFSViewedEvent();
        }}
        PaperProps={{
          id: 'freeTrialUpgradeModal',
          sx: classes.dialogContainer,
        }}
      >
        {isXS && <AdvisoryCloudLogo />}
        {isXS && (
          <Close
            onClick={() => {
              if (loading) return;
              handleClose();
              sendFSViewedEvent();
            }}
            sx={{
              position: 'absolute',
              top: '12px',
              right: '8px',
              cursor: 'pointer',
              color: 'black',
              fontSize: '24px',
            }}
          />
        )}
        <DialogTitle
          sx={{
            fontSize: { xxs: '22px', sm: '32px' },
            padding: { xxs: '8px 12px', sm: '8px 24px' },
          }}
        >
          Upgrade to Unlock Full Access
        </DialogTitle>
        <DialogContentText sx={{ fontSize: { xxs: '15px', sm: '16px' } }}>
          Most clients upgrade to an Annual plan to show boards they're
          committed long-term
        </DialogContentText>
        <Box sx={{ mt: 2, width: '100%', textAlign: 'center' }}>
          <Grid container>
            <Grid item xxs={12} display={{ xxs: 'block', sm: 'none' }}>
              <Typography fontStyle={'italic'} mb="12px">
                Which plan is right for you?
              </Typography>
            </Grid>
            {/* Header Row */}
            <Grid item xxs={3} sm={4}>
              <Typography sx={classes.textHeadline}>Compare Plans</Typography>
              <Typography sx={{ display: { xxs: 'none', sm: 'block' } }}>
                Which plan is right for you?
              </Typography>
            </Grid>
            <Grid item xxs={3} sm={2.666}>
              <Box sx={classes.textHeadline}>Current</Box>
              <Button
                id="currentUpgradeButton"
                className="ftUpgradeCTA"
                variant="outlined"
                size="small"
                sx={{
                  ...classes.upgradeButton,
                  borderColor: 'white',
                  fontSize: { xxs: '11px', md: '14px' },
                  fontWeight: 400,
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'white',
                  },
                }}
                onClick={() => {
                  setSelectedPlan(1);
                  setOpenConfirmationModal(true);
                }}
                disabled={loading}
              >
                {loading && selectedPlan === 1 ? (
                  <LoadingLogoSkeleton
                    width={{ xxs: '20px', sm: '28px' }}
                    height={{ xxs: '20px', sm: '28px' }}
                  />
                ) : (
                  'Upgrade'
                )}
              </Button>
            </Grid>
            <Grid item xxs={3} sm={2.666}>
              <Box sx={classes.textHeadline}>Annual</Box>
              <Button
                id="annualUpgradeButton"
                className="ftUpgradeCTA"
                variant="contained"
                size="small"
                sx={classes.upgradeButton}
                onClick={() => {
                  setSelectedPlan(2);
                  setOpenConfirmationModal(true);
                }}
                disabled={loading}
              >
                {loading && selectedPlan === 2 ? (
                  <LoadingLogoSkeleton
                    width={{ xxs: '20px', sm: '28px' }}
                    height={{ xxs: '20px', sm: '28px' }}
                  />
                ) : (
                  'Upgrade'
                )}
              </Button>
            </Grid>
            <Grid item xxs={3} sm={2.666}>
              <Box sx={classes.textHeadline}>VIP</Box>
              <Button
                id="vipUpgradeButton"
                className="ftUpgradeCTA"
                variant="contained"
                size="small"
                sx={classes.upgradeButton}
                onClick={() => {
                  // VIP plan harcoded in backend-services
                  setSelectedPlan(3);
                  setOpenConfirmationModal(true);
                }}
                disabled={loading}
              >
                {loading && selectedPlan === 3 ? (
                  <LoadingLogoSkeleton
                    width={{ xxs: '20px', sm: '28px' }}
                    height={{ xxs: '20px', sm: '28px' }}
                  />
                ) : (
                  'Upgrade'
                )}
              </Button>
            </Grid>
            {/* Data Rows */}
            <Grid item xxs={3} sm={4}>
              <Box
                sx={{
                  ...classes.cell,
                  height: { xxs: '110px', xs: '88px', sm: '76px' },
                }}
              >
                Price
              </Box>
            </Grid>
            <Grid item xxs={3} sm={2.666}>
              <Typography
                sx={{
                  ...classes.cell,
                  display: 'flex',
                  flexDirection: 'column',
                  height: { xxs: '110px', xs: '88px', sm: '76px' },
                }}
              >
                ${currentPlan.recurlyPlanCodePrice}/Month
                <span style={{ fontSize: '12px' }}>After 14 days</span>
              </Typography>
            </Grid>
            <Grid item xxs={3} sm={2.666}>
              <Typography
                sx={{
                  ...classes.cell,
                  display: 'flex',
                  flexDirection: 'column',
                  height: { xxs: '110px', xs: '88px', sm: '76px' },
                }}
              >
                ${(upgradePlan.recurlyPlanCodePrice / 12).toFixed(0)}/Month
                <span style={{ fontWeight: '600' }}>
                  Billed at ${upgradePlan.recurlyPlanCodePrice}/Year
                </span>
                <span style={{ fontSize: '12px' }}>Save ${getAnualSave()}</span>
              </Typography>
            </Grid>
            <Grid item xxs={3} sm={2.666}>
              <Typography
                sx={{
                  ...classes.cell,
                  display: 'flex',
                  flexDirection: 'column',
                  height: { xxs: '110px', xs: '88px', sm: '76px' },
                }}
              >
                $246/Month
                <span style={{ fontWeight: '600' }}>Billed at $2950/Year</span>
                <span style={{ fontSize: '12px' }}>
                  Best for Busy Executives
                </span>
              </Typography>
            </Grid>
            <Grid item xxs={3} sm={4}>
              <Box
                sx={{ ...classes.cell, fontSize: { xxs: '11px', sm: '14px' } }}
              >
                # of Advisory Boards You Can Join
              </Box>
            </Grid>
            <Grid item xxs={3} sm={2.666}>
              <Box sx={classes.cell}>
                {!currentPlan?.boardsCanJoinCount
                  ? ''
                  : currentPlan.boardsCanJoinCount === -1
                  ? 'Unlimited'
                  : currentPlan.boardsCanJoinCount === 1
                  ? '1 Board'
                  : `${currentPlan.boardsCanJoinCount} Boards`}
              </Box>
            </Grid>
            <Grid item xxs={3} sm={2.666}>
              <Box sx={classes.cell}>Unlimited</Box>
            </Grid>
            <Grid item xxs={3} sm={2.666}>
              <Box sx={classes.cell}>Unlimited</Box>
            </Grid>
            <Grid item xxs={3} sm={4}>
              <Box
                sx={{ ...classes.cell, fontSize: { xxs: '11px', sm: '14px' } }}
              >
                Shows Boards You're Committed Long-Term
              </Box>
            </Grid>
            <Grid item xxs={3} sm={2.666}>
              <Box sx={classes.cell}>
                <Close sx={classes.closeIcon} />
              </Box>
            </Grid>
            <Grid item xxs={3} sm={2.666}>
              <Box sx={classes.cell}>
                <ACCheckCircleIcon />
              </Box>
            </Grid>
            <Grid item xxs={3} sm={2.666}>
              <Box sx={classes.cell}>
                <ACCheckCircleIcon />
              </Box>
            </Grid>
            <Grid item xxs={3} sm={4}>
              <Box
                sx={{ ...classes.cell, fontSize: { xxs: '11px', sm: '14px' } }}
              >
                Featured Listing in Advisor Directory
              </Box>
            </Grid>
            <Grid item xxs={3} sm={2.666}>
              <Box sx={classes.cell}>
                <Close sx={classes.closeIcon} />
              </Box>
            </Grid>
            <Grid item xxs={3} sm={2.666}>
              <Box sx={classes.cell}>
                <ACCheckCircleIcon />
              </Box>
            </Grid>
            <Grid item xxs={3} sm={2.666}>
              <Box sx={classes.cell}>
                <ACCheckCircleIcon />
              </Box>
            </Grid>
            <Grid item xxs={3} sm={4}>
              <Box
                sx={{ ...classes.cell, fontSize: { xxs: '11px', sm: '14px' } }}
              >
                Personal Assistance
              </Box>
            </Grid>
            <Grid item xxs={3} sm={2.666}>
              <Box sx={classes.cell}>
                <Close sx={classes.closeIcon} />
              </Box>
            </Grid>
            <Grid item xxs={3} sm={2.666}>
              <Box sx={classes.cell}>
                <Close sx={classes.closeIcon} />
              </Box>
            </Grid>
            <Grid item xxs={3} sm={2.666}>
              <Box sx={classes.cell}>
                Quarterly
                <br />
                Check-Ins
              </Box>
            </Grid>
            <Grid item xxs={12}>
              <Typography fontStyle={'italic'} mt="12px">
                When you click Upgrade your card on file will be charged
                immediately and you'll gain full access.
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Dialog>
      <Dialog
        open={openConfirmationModal}
        onClose={() => setOpenConfirmationModal(false)}
        PaperProps={{
          id: 'freeTrialUpgradeConfirmationModal',
          sx: { borderRadius: 3, minWidth: 320, textAlign: 'center' },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: { xxs: '22px', sm: '32px' },
            padding: { xxs: '8px 12px', sm: '8px 24px' },
          }}
        >
          Confirm Upgrade
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to upgrade to the{' '}
            <b>{getSelectedPlanLabel()}</b> plan?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            id="cancelUpgradeButton"
            variant="outlined"
            onClick={() => setOpenConfirmationModal(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            id="confirmUpgradeButton"
            variant="contained"
            onClick={() => {
              setOpenConfirmationModal(false);
              upgradeCall();
            }}
            disabled={loading}
          >
            {loading ? <LoadingLogoSkeleton /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
      <ACSnackbar
        open={snackbarSuccess}
        text={snackMessage || ''}
        severity={SnackbarType.SUCCESS}
        onClose={() => {
          setSnackbarSuccess(false);
        }}
        autoHideDuration={6000}
      />
      <ACSnackbar
        open={snackbarError}
        text={snackMessage || ''}
        severity={SnackbarType.ERROR}
        onClose={() => {
          setSnackbarError(false);
        }}
        autoHideDuration={6000}
      />
    </>
  );
};

export default NewFreeTrialModal;
